/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import testid from '../../utils/testeId'
import { ProgressIndicator } from '@veneer/core'
import { GlobalHeader } from '../shared-components/GlobalHeader/index'
import {
  checkAccessPermissions,
  getDateandTime,
  setProps,
  setupPermissionChecker
} from '../../utils/commonMethods'
import { getEmail, isServiceCustomer } from '../../utils/api'
import useShellRootContext from '../../contexts/ShellRoot/useShellRootContext'
import {
  NOTIFICATIONS_ACCESS_PERM,
  DEVICES_ACCESS_PERM,
  DEVICE_POLICY_ACCESS_PERM,
  USERS_ACCESS_PERM,
  ENDPOINT_SECURITY_ACCESS_PERM,
  ACTIVE_USER_RESOURCE_ID
} from '../../utils/constants'
import {
  ActiveUserType,
  DashBoardIsDisplayed,
  RefreshButtonClicked,
  publishEvent
} from 'src/utils/analytics'
import * as S from './styles'

import FirstRowWidgets from './RowLayout/Normal/firstRowWidgets'
import SecondRowWidgets from './RowLayout/Normal/secondRowWidgets'
import ThirdRowWidgets from './RowLayout/Normal/thirdRowWidgets'
import FourthRowWidgets from './RowLayout/Normal/fourthRowWidgets'
import FifthRowWidgets from './RowLayout/Normal/fifthRowWidgets'

import FirstRowWidgetsWide from './RowLayout/WideScreen/firstRowWidgetsWide'
import SecondRowWidgetsWide from './RowLayout/WideScreen/secondRowWidgetsWide'
import ThirdRowWidgetsWide from './RowLayout/WideScreen/thirdRowWidgetsWide'

export const Home = (props) => {
  setProps(props)

  const { t = (key, defaultValue) => defaultValue || key, interfaces } =
    useShellRootContext?.() || {}
  const accessControl = interfaces?.v1?.accessControl || props.mockAccessControl
  const analytics = interfaces?.v1?.analytics || props.mockAnalytics
  const authProvider = interfaces?.v2.authProvider || props.mockAuthProvider
  const authStack = interfaces?.v1?.app?.getAuthStack?.() || props.mockStack
  const breadcrumbs = interfaces?.v1?.breadcrumbs || props.mockBreadcrumbs
  const criterion = interfaces?.v1?.criterion || props.mockCriterion
  const events = interfaces?.v1?.events || props.mockEvents

  const useWindowSize = () => {
    const [windowSize, setWindowSize] = useState(window.innerWidth)
    const callback = () => setWindowSize(window.innerWidth)

    useEffect(() => {
      window.addEventListener('resize', callback)
      return () => window.removeEventListener('resize', callback)
    }, [])

    return windowSize
  }

  const resolution = useWindowSize()

  const isWideResolution = resolution > 1400

  const [dateAndTime, setDateAndTime] = useState<string>(getDateandTime())
  const [notificationsAccessState, setNotificationsAccessState] =
    useState<boolean>(false)
  const [devicesAccessState, setDevicesAccessState] = useState<boolean>(false)
  const [devicePolicyAccessState, setDevicePolicyAccessState] =
    useState<boolean>(false)
  const [usersAccessState, setUsersAccessState] = useState<boolean>(false)
  const [endPtSecurityAccessState, setEndPtSecurityAccessState] =
    useState<boolean>(false)
  const [startingLoader, setStartingLoader] = useState<boolean>(false)
  const [loggedInUser, setLoggedInUser] = useState<string>('')
  const solutionsAccessState = true // currently not having any scopes

  const activeUser: ActiveUserType = {
    resourceId: ''
  }

  useEffect(() => {
    const orgedAuthProvider = authProvider?.createOrgedAuthProvider?.()
    const getActiveUserDetails = async () => {
      await getEmail(authStack, orgedAuthProvider).then((result) => {
        setLoggedInUser(result.givenName)
        activeUser.resourceId = result.resourceId
      })
      if (
        window.sessionStorage.getItem(ACTIVE_USER_RESOURCE_ID) !==
        activeUser.resourceId
      ) {
        window.sessionStorage.setItem(
          ACTIVE_USER_RESOURCE_ID,
          activeUser.resourceId
        )
        publishEvent?.(analytics, DashBoardIsDisplayed())
      }
    }
    getActiveUserDetails()
  }, [analytics, authProvider, authStack])

  useEffect(() => {
    const permissionConfigs = [
      {
        scope: NOTIFICATIONS_ACCESS_PERM,
        setResponse: setNotificationsAccessState
      },
      {
        scope: DEVICES_ACCESS_PERM,
        setResponse: setDevicesAccessState
      },
      {
        scope: DEVICE_POLICY_ACCESS_PERM,
        setResponse: setDevicePolicyAccessState
      },
      { scope: USERS_ACCESS_PERM, setResponse: setUsersAccessState },
      {
        scope: ENDPOINT_SECURITY_ACCESS_PERM,
        setResponse: setEndPtSecurityAccessState
      }
    ]

    setStartingLoader(true)

    permissionConfigs.forEach(({ scope, setResponse }) => {
      const { checkPermissions, formattedScope } = setupPermissionChecker(
        accessControl,
        scope
      )
      checkAccessPermissions({
        checkPermissions,
        scope: formattedScope,
        setResponse
      })
    })

    setTimeout(() => {
      setStartingLoader(false)
    }, 2000)
  }, [accessControl])

  const renderStyledLoader = () => {
    return (
      <S.StyledDiv>
        <ProgressIndicator data-testid="jarvis-react-ecp-dashboard__loader" />
      </S.StyledDiv>
    )
  }

  const renderNotificationsWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          component="@jarvis/react-ecp-notification-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderStatusWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          component="@jarvis/react-ecp-status-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderDeviceStatusWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          component="@jarvis/react-ecp-device-status-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderDevicePolicyStatusWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          resolution={resolution}
          component="@jarvis/react-ecp-device-policy-status-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime, resolution])

  const renderCustomersUsersStatusWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          component="@jarvis/react-ecp-customers-users-status-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderSecureFleetWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.Mfe
          {...props}
          component="@jarvis/react-ecp-endpoint-security-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderSolutionsWidget = useMemo(() => {
    if (startingLoader) {
      return renderStyledLoader()
    } else {
      return (
        <S.FirstRowMfe
          {...props}
          component="@jarvis/react-ecp-solutions-and-services-widget"
        />
      )
    }
  }, [props, startingLoader, dateAndTime])

  const renderPrimaryWidgets = useMemo(() => {
    const rowLayoutProps = {
      notificationsAccessState: notificationsAccessState,
      devicesAccessState: devicesAccessState,
      devicePolicyAccessState: devicePolicyAccessState,
      usersAccessState: usersAccessState,
      endPtSecurityAccessState: endPtSecurityAccessState,
      solutionsAccessState: solutionsAccessState,
      renderNotificationsWidget: renderNotificationsWidget,
      renderStatusWidget: renderStatusWidget,
      renderDeviceStatusWidget: renderDeviceStatusWidget,
      renderDevicePolicyStatusWidget: renderDevicePolicyStatusWidget,
      renderCustomersUsersStatusWidget: renderCustomersUsersStatusWidget,
      renderSecureFleetWidget: renderSecureFleetWidget,
      renderSolutionsWidget: renderSolutionsWidget
    }

    if (isWideResolution) {
      return (
        <>
          <FirstRowWidgetsWide {...rowLayoutProps} />

          <SecondRowWidgetsWide {...rowLayoutProps} />

          <ThirdRowWidgetsWide {...rowLayoutProps} />
        </>
      )
    }

    return (
      <>
        <FirstRowWidgets {...rowLayoutProps} />

        <SecondRowWidgets {...rowLayoutProps} />

        <ThirdRowWidgets {...rowLayoutProps} />

        <FourthRowWidgets {...rowLayoutProps} />

        <FifthRowWidgets {...rowLayoutProps} />
      </>
    )
  }, [
    notificationsAccessState,
    devicesAccessState,
    devicePolicyAccessState,
    usersAccessState,
    endPtSecurityAccessState,
    solutionsAccessState,
    renderNotificationsWidget,
    renderStatusWidget,
    renderDeviceStatusWidget,
    renderDevicePolicyStatusWidget,
    renderCustomersUsersStatusWidget,
    renderSecureFleetWidget,
    renderSolutionsWidget,
    isWideResolution
  ])

  const [pageTitle, setPageTitle] = useState<string>()
  const [serviceCustomer, setServiceCustomer] = useState<boolean>(false)

  useEffect(() => {
    const getServiceCustomer = async () => {
      const response = await isServiceCustomer(criterion)

      setServiceCustomer(response)
    }

    getServiceCustomer()

    if (props.properties.useOrgedAuthProvider) {
      serviceCustomer === true
        ? setPageTitle(`${loggedInUser}`)
        : setPageTitle(
            `${t('ecp-dashboard.welcome', 'Welcome ,')}, ${loggedInUser}!`
          )
    } else {
      setPageTitle(' ')

      breadcrumbs.add({
        key: 'customer-dashboard-next-level',
        text: t('ecp-dashboard.dashboard', 'Dashboard'),
        translationKey: undefined,
        url: undefined
      })
    }
  }, [
    props.properties.useOrgedAuthProvider,
    loggedInUser,
    serviceCustomer,
    t,
    breadcrumbs,
    criterion
  ])

  const refreshData = useCallback(() => {
    setDateAndTime(getDateandTime())

    publishEvent?.(analytics, RefreshButtonClicked())

    return dateAndTime
  }, [dateAndTime])

  useEffect(() => {
    events.addEventListener('ecp-banner-reload-call', refreshData)

    return () => {
      events.removeEventListener('ecp-banner-reload-call', refreshData)
    }
  }, [events])

  return (
    <>
      <S.StyledGlobalHeader>
        <GlobalHeader title={pageTitle} />
      </S.StyledGlobalHeader>

      <S.Container data-testid={testid('dashboard')}>
        {renderPrimaryWidgets}
      </S.Container>
    </>
  )
}

export default Home
